import { dataset, projectId } from '@/sanity/lib/api'
import createImageUrlBuilder from '@sanity/image-url'
import type { Image } from 'sanity'

const imageBuilder = createImageUrlBuilder({
  projectId: projectId || '',
  dataset: dataset || '',
})

export const urlForImage = (source: Image | null | undefined) => {
  // Ensure that source image contains a valid reference
  if (!source?.asset?._ref) {
    return undefined
  }

  return imageBuilder?.image(source).auto('format').fit('max')
}

export function urlForOpenGraphImage(image: Image | null | undefined) {
  return urlForImage(image)?.width(1200).height(627).fit('crop').url()
}

export function resolveHref(documentType?: string, slug?: string | null): string | undefined {
  switch (documentType) {
    case 'home':
      return '/'
    case 'page':
      return slug ? `/${slug}` : undefined
    case 'project':
      return slug ? `/projects/${slug}` : undefined
    case 'notice':
      return slug ? `/community/notice/${slug}` : undefined
    case 'press':
      return slug ? `/community/press/${slug}` : undefined
    default:
      console.warn('Invalid document type:', documentType)
      return undefined
  }
}

interface FilmForRelated {
  _id: string
  title: string | null
  title_kr?: string | null
  thumbnail?: any
  slug: string | null
  year?: number | null
  genre?: string[] | null
  section?: Array<{ _id: string; title: string | null; slug: string | null }> | null
  director?: Array<{ _id: string; title: string | null; title_kr?: string | null }> | null
  color?: string | null
  subtitles?: string | string[] | null
  rating?: string | null
  runtime?: { minutes?: number; seconds?: number } | null
  premiere?: string | null
  year_ref?: { _ref: string } | null
  sectionRefs?: string[] | null
  directorRefs?: string[] | null
}

interface CurrentFilm {
  _id: string
  year_ref?: { _ref: string } | null
  section?: Array<{ _id: string | null; _ref: string }> | null
  director?: Array<{ _id: string | null; _ref: string }> | null
}

export function combineRelatedFilms(
  currentFilm: CurrentFilm | null | undefined,
  allRelatedFilms: FilmForRelated[] | null | undefined,
  maxCount = 12
): FilmForRelated[] {
  if (!currentFilm || !allRelatedFilms?.length) {
    return []
  }

  const result: FilmForRelated[] = []
  const used = new Set<string>()

  const currentYearRef = currentFilm.year_ref?._ref
  const currentSectionRefs = currentFilm.section?.map(s => s._ref) || []
  const currentDirectorRefs = currentFilm.director?.map(d => d._ref) || []

  // 우선순위별로 영화 분류
  const sameYearAndSection: FilmForRelated[] = []
  const sameSection: FilmForRelated[] = []
  const sameDirector: FilmForRelated[] = []
  const others: FilmForRelated[] = []

  allRelatedFilms.forEach(film => {
    if (film._id === currentFilm._id) return
    // 필수 필드가 없는 영화는 제외
    if (!film.title || !film.slug) return

    const isSameYear = film.year_ref?._ref === currentYearRef
    const hasSameSection = film.sectionRefs?.some(ref => currentSectionRefs.includes(ref))
    const hasSameDirector = film.directorRefs?.some(ref => currentDirectorRefs.includes(ref))

    if (isSameYear && hasSameSection) {
      sameYearAndSection.push(film)
    } else if (hasSameSection) {
      sameSection.push(film)
    } else if (hasSameDirector) {
      sameDirector.push(film)
    } else {
      others.push(film)
    }
  })

  // 우선순위대로 합치기
  const categories = [sameYearAndSection, sameSection, sameDirector, others]

  for (const category of categories) {
    for (const film of category) {
      if (result.length >= maxCount) break
      if (!used.has(film._id)) {
        // 불필요한 메타데이터 제거
        const { year_ref, sectionRefs, directorRefs, ...cleanFilm } = film
        result.push(cleanFilm)
        used.add(film._id)
      }
    }
    if (result.length >= maxCount) break
  }

  return result
}
